/* frontend/src/components/ArticleDetail.js */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { getArticle, likeArticle, getComments, postComment } from '../utils/api';
import { createWebSocket } from '../utils/websocket';
import AnimatedSocialLinks from './AnimatedSocialLinks';
import {
  TwitterShareButton,
  LinkedinShareButton,
  FacebookShareButton,
  WhatsappShareButton,
} from 'react-share';
import { FaHeart } from 'react-icons/fa';
import CommentForm from './CommentForm';
import CommentList from './CommentList';
import axios from 'axios';
import '../pages/Blog.css';

const ArticleDetail = ({ user }) => {
  const { id: articleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [liked, setLiked] = useState(false);
  const [error, setError] = useState(null);

  // Fetch like status
  const fetchLikeStatus = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/articles/${articleId}/like_status/`, {
      withCredentials: true,
    });
    console.log('Like status response:', res.data);
    setLiked(res.data.liked);
  };

  useEffect(() => {
    fetchLikeStatus();
  }, [articleId]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, { withCredentials: true })
      .catch((err) => console.error('Failed to fetch CSRF token:', err));
  }, []);

  const { data: article, isLoading, error: queryError } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => getArticle(articleId).then((res) => res.data),
  });

  const { data: comments, refetch: refetchComments } = useQuery({
    queryKey: ['comments', articleId],
    queryFn: () => getComments(articleId).then((res) => res.data),
    enabled: !!article,
  });

  const likeMutation = useMutation({
    mutationFn: () => likeArticle(articleId),
    onSuccess: (data) => {
      console.log('Like mutation response:', data);
      setError(null);
      queryClient.setQueryData(['article', articleId], (old) => ({
        ...old,
        likes_count: data.likes_count,
      }));
      fetchLikeStatus();
    },
    onError: (error) => {
      console.error('Like error:', error.response?.data || error.message);
      let errorMessage = 'Failed to like article.';
      if (error.response?.status === 404) {
        errorMessage = 'Article not found.';
      } else if (error.response?.data?.errors) {
        errorMessage = JSON.stringify(error.response.data.errors);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setError(errorMessage);
      fetchLikeStatus(); // Retry fetching like status to update UI
    },
  });

  const shareMutation = useMutation({
    mutationFn: (platform) =>
      axios.post(
        `${process.env.REACT_APP_API_URL}/api/articles/${articleId}/share/`,
        { platform },
        { withCredentials: true }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['article', articleId], (old) => ({
        ...old,
        shares_count: data.data.shares_count,
      }));
    },
    onError: (error) => {
      console.error('Share error:', error);
      setError('Failed to record share.');
    },
  });

  useEffect(() => {
    if (queryError && queryError.response?.status === 404) {
      navigate('/blog', { replace: true });
    }
  }, [queryError, navigate]);

  useEffect(() => {
    if (article) {
      const ws = createWebSocket(articleId, (data) => {
        if (data.comment) {
          queryClient.setQueryData(['comments', articleId], (old) => [...(old || []), data.comment]);
        }
      });
      return () => ws.close();
    }
  }, [article, articleId, queryClient]);

  if (isLoading) return <div className="text-center">Loading...</div>;
  if (queryError) return null;

  const articleUrl = `https://serrand.org/blog/${article.id}`;
  const shareContent = `${article.title}\n${article.excerpt}\n${articleUrl}`;

  return (
    <div className="linkedin-blog">
      <div className="container grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <motion.div
            className="article-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-bold mb-2">{article.title}</h1>
            <p className="text-[#666666] mb-4">
              By {article.author_username} • {new Date(article.created_at).toLocaleDateString()}
            </p>
            <div className="flex gap-4 text-sm text-[#666666] mb-4">
              <span>{article.view_count} views</span>
              <span>{article.likes_count} likes</span>
              <span>{article.shares_count} shares</span>
            </div>
            {article.thumbnail && (
              <img src={article.thumbnail} alt={article.title} className="w-full rounded-lg mb-6" />
            )}
            {error && (
              <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{error}</div>
            )}
            <div
              className="prose prose-lg text-[#333333]"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            <div className="flex gap-4 mt-6">
              <motion.button
                onClick={() => likeMutation.mutate()}
                className={`like-button ${liked ? 'liked' : ''}`}
                whileTap={{ scale: 1.3 }}
                animate={liked ? { scale: [1, 1.4, 1], rotate: [0, 10, -10, 0] } : { scale: 1, rotate: 0 }}
                transition={{ duration: 0.3 }}
              >
                <FaHeart className={liked ? 'text-red-500 fill-current' : 'text-gray-500'} />
              </motion.button>
              <TwitterShareButton
                url={articleUrl}
                title={shareContent}
                onShareWindowClose={() => shareMutation.mutate('twitter')}
              >
                <i className="fab fa-x-twitter social-icon" />
              </TwitterShareButton>
              <LinkedinShareButton
                url={articleUrl}
                title={article.title}
                summary={article.excerpt}
                source="Serrand Blog"
                onShareWindowClose={() => shareMutation.mutate('linkedin')}
              >
                <i className="fab fa-linkedin-in social-icon" />
              </LinkedinShareButton>
              <FacebookShareButton
                url={articleUrl}
                quote={shareContent}
                onShareWindowClose={() => shareMutation.mutate('facebook')}
              >
                <i className="fab fa-facebook-f social-icon" />
              </FacebookShareButton>
              <WhatsappShareButton
                url={articleUrl}
                title={shareContent}
                onShareWindowClose={() => shareMutation.mutate('whatsapp')}
              >
                <i className="fab fa-whatsapp social-icon" />
              </WhatsappShareButton>
            </div>
            <hr className="my-6" />
            {user ? (
              <>
                <CommentForm articleId={articleId} refetchComments={refetchComments} />
                <CommentList comments={comments || []} />
              </>
            ) : (
              <p className="text-[#666666]">Please log in to comment.</p>
            )}
          </motion.div>
        </div>
        <motion.div
          className="sidebar bg-gray-800 rounded-lg shadow-md p-6"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-xl font-bold mb-4">Get In Touch :)</h2>
          <AnimatedSocialLinks className="flex flex-col gap-2" />
        </motion.div>
      </div>
    </div>
  );
};

export default ArticleDetail;
